import { defineStore } from 'pinia';

import { Analysis } from '@/Analysis/analysis.entity';
import AnalysisService from '@/Analysis/analysis.service';
import { useAnalysisActionsStore } from '@/stores/analysis-actions.store';

export type DuplicateWarning = {
    fileName: string;
    uploadedAnalysisId: string;
    similar: Analysis[];
};

// Data layer for the similar analyses lists. Owns the similar-analyses
// ("duplicate decision") flows and the shared delete;
//
// - `warnings`: the grouped duplicate warnings shown in the upload/list modal.
// - `similar`: the flat similar-analyses set for a single analysis (AnalysisForm tab).
type SimilarAnalysesState = {
    warnings: DuplicateWarning[];
    similar: Analysis[];
    isDuplicateDecisionModalOpen: boolean;
};

// Removes an analysis from a list in place (splice, not reassignment) so lists that share
// a reference — e.g. the active `similar` list handed a warning group's own array — all
// reflect the removal from a single call.
const removeAnalysis = (list: Analysis[], analysisId: string): void => {
    const index = list.findIndex((analysis) => analysis.analysisId === analysisId);
    if (index !== -1) {
        list.splice(index, 1);
    }
};

export const useSimilarAnalysesStore = defineStore('similarAnalyses', {
    state: (): SimilarAnalysesState => ({
        warnings: [],
        similar: [],
        isDuplicateDecisionModalOpen: false,
    }),
    getters: {
        hasWarnings(state: SimilarAnalysesState): boolean {
            return state.warnings.length > 0;
        },
    },
    actions: {
        // Modal expansion: point the active similar list at a warning group's matches by
        // SHARED REFERENCE (not a copy), so an in-place deletion updates this list and the
        // group's own array — and its count — from one call. Only one group is shown at a time.
        showSimilar(similar: Analysis[]): void {
            this.similar = similar;
        },
        // AnalysisForm's "Similar" tab: the flat similar set for the viewed analysis.
        async loadSimilar(analysisId: string): Promise<void> {
            const analysisService = new AnalysisService();
            this.similar = await analysisService.findSimilarAnalyses(analysisId);
        },
        // Upload flow: check a settled batch of uploads, merging results into any warnings
        // already shown so a second batch doesn't drop the first.
        async checkUploads(batch: { analysisId: string; fileName: string }[]): Promise<void> {
            const byUpload = new Map(this.warnings.map((warning) => [warning.uploadedAnalysisId, warning]));
            const analysisService = new AnalysisService();
            for (const { analysisId, fileName } of batch) {
                const similar = await analysisService.findSimilarAnalyses(analysisId);
                if (similar.length > 0) {
                    byUpload.set(analysisId, { fileName, uploadedAnalysisId: analysisId, similar });
                }
            }
            this.warnings = [...byUpload.values()];
            this.openDuplicateDecisionModal();
        },

        // List flow: open the modal for a single existing row.
        async checkAnalysisIdForSimilarAnalyses(analysisId: string, fileName: string): Promise<void> {
            const analysisService = new AnalysisService();
            const similar = await analysisService.findSimilarAnalyses(analysisId);
            this.warnings = [{ fileName, uploadedAnalysisId: analysisId, similar }];
            this.openDuplicateDecisionModal();
        },
        openDuplicateDecisionModal(): void {
            if (this.hasWarnings) this.isDuplicateDecisionModalOpen = true;
        },
        closeDuplicateDecisionModal(): void {
            this.isDuplicateDecisionModalOpen = false;
            this.dismissWarnings();
        },
        clearSimilar(): void {
            this.showSimilar([]);
        },
        // The single delete call shared by the row action, the list modal and the upload
        // flow. Reconciles the open warnings whether the deleted analysis is a flagged
        // decision itself or one of its similar matches, pruning any group left with no
        // remaining matches. UI concerns (confirmation, toasts, list refresh) stay in the
        // caller.
        //
        // NOTE: callers should pretty much always refresh the analyses list after this
        // (today via the view's own `refreshAnalyses`), since a deletion changes the rows
        // and their similar-analysis counts. Once `findAll` moves into this store, fold
        // that refresh in here so it happens once, in one place, instead of per caller.
        async deleteAnalysis(analysisId: string): Promise<void> {
            await useAnalysisActionsStore().deleteAnalysis(analysisId);

            // Strip the analysis from every similar list in place. The active `similar` list
            // may share a reference with one of the warning groups (see showSimilar); doing it
            // in place keeps them, and the group's count, in sync from a single removal.
            removeAnalysis(this.similar, analysisId);
            this.warnings.forEach((warning) => removeAnalysis(warning.similar, analysisId));

            // Drop a flagged decision outright, and any group left with no remaining matches.
            this.warnings = this.warnings.filter(
                (warning) => warning.uploadedAnalysisId !== analysisId && warning.similar.length > 0,
            );
        },
        dismissWarnings(): void {
            this.warnings = [];
        },
    },
});
